import { useState, useEffect } from 'react'
import {
  CreditCard,
  Download,
  Calendar,
  Filter,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  ArrowUpRight,
  Wallet,
  BookOpen,
  Video,
  Receipt,
  TrendingUp
} from 'lucide-react'
import jsPDF from 'jspdf'

interface Payment {
  _id: string
  razorpayPaymentId: string
  razorpayOrderId: string
  razorpaySignature: string
  amount: number
  currency: string
  status: 'completed' | 'failed' | 'pending'
  type: 'course' | 'live-session'
  createdAt: string
  course?: {
    _id: string
    title: string
  }
  liveSession?: {
    _id: string
    title: string
  }
}

export default function UserPayments() {
  const [payments, setPayments] = useState<Payment[]>([])
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState<'all' | 'completed' | 'failed' | 'pending'>('all')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchPayments()
  }, [])

  const fetchPayments = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch('/api/payments', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!response.ok) throw new Error('Failed to fetch payments')

      const data = await response.json()
      setPayments(data.payments)
    } catch (error) {
      console.error('Error fetching payments:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredPayments = payments.filter(payment => {
    const matchesStatus = filterStatus === 'all' || payment.status === filterStatus
    const matchesSearch =
      searchQuery === '' ||
      payment.razorpayPaymentId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (payment.course?.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (payment.liveSession?.title || '').toLowerCase().includes(searchQuery.toLowerCase())

    return matchesStatus && matchesSearch
  })

  const handleDownloadReceipt = (payment: Payment) => {
    try {
      const pdf = new jsPDF()
      const primaryColor: [number, number, number] = [225, 29, 72]
      const secondaryColor: [number, number, number] = [75, 85, 99]
      const textColor: [number, number, number] = [31, 41, 55]

      pdf.setFillColor(...primaryColor)
      pdf.rect(0, 0, 210, 40, 'F')

      pdf.setTextColor(255, 255, 255)
      pdf.setFontSize(24)
      pdf.setFont('helvetica', 'bold')
      pdf.text("SUDHAS ACADEMY", 105, 20, { align: 'center' })

      pdf.setFontSize(12)
      pdf.setFont('helvetica', 'normal')
      pdf.text('Payment Receipt', 105, 32, { align: 'center' })

      pdf.setTextColor(textColor[0], textColor[1], textColor[2])
      let yPosition = 60

      pdf.setFontSize(14)
      pdf.setFont('helvetica', 'bold')
      pdf.text('Transaction Details', 20, yPosition)
      yPosition += 15

      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'normal')

      const details = [
        ['Payment ID:', payment.razorpayPaymentId],
        ['Order ID:', payment.razorpayOrderId],
        ['Amount:', `${Math.abs(Math.round(Number(String(payment.amount).replace(/[^\d.-]/g, ''))))}`],
        ['Currency:', payment.currency.toUpperCase()],
        ['Status:', payment.status.charAt(0).toUpperCase() + payment.status.slice(1)],
        ['Date:', new Date(payment.createdAt).toLocaleDateString('en-IN', {
          year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
        })],
        ['Type:', payment.type === 'course' ? 'Course Purchase' : 'Live Session'],
        ['Item:', payment.course?.title || payment.liveSession?.title || 'Unknown']
      ]

      details.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold')
        pdf.text(label, 20, yPosition)
        pdf.setFont('helvetica', 'normal')
        pdf.text(value, 80, yPosition)
        yPosition += 8
      })

      yPosition += 10
      pdf.setFontSize(12)
      pdf.setFont('helvetica', 'bold')

      if (payment.status === 'completed') {
        pdf.setFillColor(34, 197, 94)
        pdf.setTextColor(255, 255, 255)
        pdf.rect(20, yPosition - 5, 25, 8, 'F')
        pdf.text('PAID', 32.5, yPosition, { align: 'center' })
      } else if (payment.status === 'failed') {
        pdf.setFillColor(239, 68, 68)
        pdf.setTextColor(255, 255, 255)
        pdf.rect(20, yPosition - 5, 35, 8, 'F')
        pdf.text('✗ FAILED', 37.5, yPosition, { align: 'center' })
      } else {
        pdf.setFillColor(245, 158, 11)
        pdf.setTextColor(0, 0, 0)
        pdf.rect(20, yPosition - 5, 40, 8, 'F')
        pdf.text('⏳ PENDING', 40, yPosition, { align: 'center' })
      }

      pdf.setTextColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
      pdf.setFontSize(8)
      pdf.setFont('helvetica', 'normal')

      const footerY = 280
      pdf.text('This is an official receipt from Sudha\'s Academy', 105, footerY, { align: 'center' })
      pdf.text('Generated on ' + new Date().toLocaleString('en-IN'), 105, footerY + 5, { align: 'center' })
      pdf.text('For support, contact: hello@sudhasacademy.com', 105, footerY + 10, { align: 'center' })

      const fileName = `receipt-${payment.razorpayPaymentId}.pdf`
      pdf.save(fileName)

    } catch (error) {
      console.error('Error generating PDF receipt:', error)
      alert('Failed to generate receipt. Please try again.')
    }
  }

  const calculateTotalSpent = () => {
    return payments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative">
          <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-2 bg-violet-600 rounded-full"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8 pb-20">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="text-4xl font-black text-slate-900 dark:text-white mb-2 tracking-tight">
            Financial <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">Overview</span>
          </h1>
          <p className="text-slate-600 dark:text-slate-400 max-w-xl text-lg">
            Track your investments in your education.
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Spent Card */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 text-white shadow-xl">
          <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-white/10 rounded-xl backdrop-blur-md">
                <Wallet className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-sm font-medium text-slate-300">Total Invested</span>
            </div>
            <div className="text-3xl font-bold mb-1">
              ₹{calculateTotalSpent().toLocaleString()}
            </div>
            <div className="flex items-center gap-1 text-xs text-emerald-400">
              <TrendingUp className="w-3 h-3" />
              <span>Lifetime value</span>
            </div>
          </div>
        </div>

        {/* Transactions Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-100 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-violet-100 dark:bg-violet-900/30 rounded-xl">
              <Receipt className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </div>
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Transactions</span>
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
            {payments.length}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Total payment records
          </div>
        </div>

        {/* Success Rate Card */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl p-6 border border-slate-100 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
              <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Success Rate</span>
          </div>
          <div className="text-3xl font-bold text-slate-900 dark:text-white mb-1">
            {payments.length > 0
              ? Math.round((payments.filter(p => p.status === 'completed').length / payments.length) * 100)
              : 0}%
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            Completed transactions
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white dark:bg-slate-800 p-2 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
          />
        </div>
        <div className="flex gap-1 bg-slate-100 dark:bg-slate-700/50 p-1 rounded-xl">
          {(['all', 'completed', 'failed'] as const).map((status) => (
            <button
              key={status}
              onClick={() => setFilterStatus(status as any)}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all ${filterStatus === status
                  ? 'bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
                }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Transactions Feed */}
      <div className="space-y-4">
        {filteredPayments.length > 0 ? (
          filteredPayments.map((payment, index) => (
            <div
              key={payment._id}
              className="group bg-white dark:bg-slate-800 rounded-2xl p-4 sm:p-6 border border-slate-100 dark:border-slate-700 hover:shadow-lg hover:shadow-slate-200/50 dark:hover:shadow-none transition-all duration-300 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Icon */}
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${payment.type === 'course'
                  ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-400'
                  : 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'
                }`}>
                {payment.type === 'course' ? <BookOpen className="w-6 h-6" /> : <Video className="w-6 h-6" />}
              </div>

              {/* Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-bold text-slate-900 dark:text-white truncate">
                    {payment.course?.title || payment.liveSession?.title || 'Unknown Item'}
                  </h3>
                  {payment.status === 'completed' && (
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400 font-mono">
                  <span>{new Date(payment.createdAt).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  <span>•</span>
                  <span>ID: {payment.razorpayPaymentId.slice(-8)}</span>
                </div>
              </div>

              {/* Amount & Action */}
              <div className="flex items-center justify-between w-full sm:w-auto gap-6">
                <div className="text-right">
                  <div className={`text-lg font-bold ${payment.status === 'failed' ? 'text-red-500 line-through decoration-2' : 'text-slate-900 dark:text-white'
                    }`}>
                    ₹{payment.amount.toLocaleString()}
                  </div>
                  <div className={`text-xs font-medium ${payment.status === 'completed' ? 'text-emerald-500' :
                      payment.status === 'failed' ? 'text-red-500' : 'text-yellow-500'
                    }`}>
                    {payment.status.toUpperCase()}
                  </div>
                </div>

                {payment.status === 'completed' && (
                  <button
                    onClick={() => handleDownloadReceipt(payment)}
                    className="p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
                    title="Download Receipt"
                  >
                    <Download className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-20 bg-white dark:bg-slate-800 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700">
            <div className="w-16 h-16 bg-slate-50 dark:bg-slate-900 rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-slate-300 dark:text-slate-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">No transactions found</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              We couldn't find any payments matching your criteria.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}